import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { MdShoppingCart } from 'react-icons/md';
import clsx from 'clsx';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { fetchComboRequest } from '../redux/slices/comboSlice';
import { PageBackButton, VegDotIcon } from '../components';
import { parsePrice, formatPrice } from '../utils/priceUtils';
import { fetchTpnConfigRequest } from '../redux/slices/tpnConfigSlice';
import { useTpnCart } from '../hooks/useTpnCart';
import { useComboCart } from '../hooks/useComboCart';

// Loading State Component
const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin h-12 w-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading Combo Details...</h2>
      <p className="text-gray-600">Please wait while we load the combo information.</p>
    </div>
  </div>
);

// Error State Component
const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="text-6xl mb-4">😞</div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Combo Not Found</h2>
      <p className="text-gray-600 mb-6">{error}</p>
      <button
        onClick={() => window.history.back()}
        className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium"
      >
        Go Back
      </button>
    </div>
  </div>
);

// Header Component
const Header: React.FC<{ onBackClick: () => void }> = ({ onBackClick }) => (
  <PageBackButton text="Home" onClick={onBackClick} />
);

// Combo Header Component
const ComboHeader: React.FC<{ 
  combo: any; 
  formatPrice: (price: string | number) => string;
}> = ({ combo, formatPrice }) => {
  const individualTotal = combo.products.reduce((total: number, product: any) => 
    total + parsePrice(product.price), 0
  );
  const comboPrice = parsePrice(combo.combo_price);

  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">{combo.title}</h1>
        {combo.offer_percentage > 0 && (
          <span 
            className="px-3 py-1 text-sm font-bold text-white bg-red-500 rounded-full"
            aria-label={`${combo.offer_percentage}% off`}
          >
            <span className="sr-only">{combo.offer_percentage}% off</span>
            {combo.offer_percentage}% OFF
          </span>
        )}
      </div>
      
      {combo.description && (
        <div className="text-gray-700 mb-3">
          <p className="text-sm sm:text-base lg:text-lg leading-relaxed whitespace-pre-wrap">
            {combo.description}
          </p>
          <div className="mt-2">
            <span className="line-through text-gray-500 text-sm sm:text-base">
              {formatPrice(individualTotal)}
            </span>
            <span className="ml-2 text-xl sm:text-2xl lg:text-3xl font-extrabold text-red-600">
              {formatPrice(comboPrice)}
            </span>
          </div>
        </div>
      )}
    </section>
  );
};

// Included Products Component
const IncludedProducts: React.FC<{ products: any[] }> = ({ products }) => (
  <section>
    <h3 className="font-semibold mb-4 text-base sm:text-lg">Included Items ({products.length})</h3>
    <div className="max-h-80 sm:max-h-96 overflow-y-auto pr-2 space-y-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
      {products.map((product, index) => (
        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex-shrink-0">
            {product.image ? (
              <img 
                src={product.image} 
                alt={product.name}
                className="w-16 h-16 object-cover rounded-lg"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = `https://via.placeholder.com/64x64/f3f4f6/9ca3af?text=${encodeURIComponent(product.name.charAt(0))}`;
                }}
              />
            ) : (
              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-xl font-bold text-gray-500">
                  {product.name.charAt(0)}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <VegDotIcon item={product} size="sm" />
              <h4 className="font-medium text-gray-900 text-sm sm:text-base">{product.name}</h4>
            </div>
            {product.description && (
              <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{product.description}</p>
            )}
            <p className="text-xs sm:text-sm font-medium text-orange-600 mt-1">
              ${parsePrice(product.price).toFixed(2)}
            </p>
          </div>
        </div>
      ))}
    </div>
  </section>
);


// Add to Cart Component
const AddToCartSection: React.FC<{
  combo: any;
  onAddToCart: () => void;
  isAddingToCart: boolean;
  formatPrice: (price: string | number) => string;
}> = ({ combo, onAddToCart, isAddingToCart, formatPrice }) => (
  <section className="pt-4 border-t border-gray-200">
    <button
      onClick={onAddToCart}
      disabled={isAddingToCart}
      aria-label={`Add ${combo.title} to cart`}
      className={clsx(
        "w-full font-semibold py-3 px-4 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
        isAddingToCart
          ? "bg-gray-400 cursor-not-allowed"
          : "bg-[#F92C37] text-white hover:bg-[#E01E2B] hover:scale-105 active:scale-95 hover:shadow-xl"
      )}
    >
      {isAddingToCart ? (
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
          Adding...
        </div>
      ) : (
        <>
          <MdShoppingCart className="w-4 h-4 mr-2" />
          Add Combo to Cart
        </>
      )}
    </button>
  </section>
);

// Left Panel Component
const LeftPanel: React.FC<{
  combo: any;
  formatPrice: (price: string | number) => string;
  onAddToCart: () => void;
  isAddingToCart: boolean;
  showAddToCart: boolean;
}> = ({ combo, formatPrice, onAddToCart, isAddingToCart, showAddToCart }) => (
  <div className="space-y-6">
    <ComboHeader combo={combo} formatPrice={formatPrice} />
    {showAddToCart && (
      <AddToCartSection 
        combo={combo}
        onAddToCart={onAddToCart}
        isAddingToCart={isAddingToCart}
        formatPrice={formatPrice}
      />
    )}
  </div>
);

// Right Panel Component
const RightPanel: React.FC<{ 
  combo: any; 
  formatPrice: (price: string | number) => string;
}> = ({ combo, formatPrice }) => (
  <aside className="space-y-6">
    <div className="relative rounded-lg overflow-hidden shadow-lg">
      {combo.combo_image ? (
        <img 
          src={combo.combo_image} 
          alt={combo.title} 
          className="w-full h-56 sm:h-72 lg:h-96 object-cover"
          loading="lazy"
          decoding="async"
          fetchPriority="high"
          onError={(e) => {
            (e.target as HTMLImageElement).src = `https://via.placeholder.com/600x400/f8f9fa/6c757d?text=${encodeURIComponent(combo.title)}`;
          }}
        />
      ) : (
        <div className="w-full h-56 sm:h-72 lg:h-96 bg-red-100 flex items-center justify-center">
          <span 
            className="text-4xl sm:text-6xl lg:text-7xl font-bold uppercase"
            style={{ color: '#FA2C37' }}
          >
            {combo.title.charAt(0)}
          </span>
        </div>
      )}
    </div>
    
    {/* Included Items Card */}
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 border border-gray-200">
      <IncludedProducts products={combo.products} />
    </div>
  </aside>
);

// Main Component
const ComboDetailPage: React.FC = () => {
  const { comboId } = useParams<{ comboId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { loading, error, data: comboData } = useAppSelector(state => state.combo);
  const { selectedRestaurant } = useAppSelector(state => state.restaurant);
  const shouldShowCart = useTpnCart(true);

  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // Use combo cart hook
  const { handleComboAddToCart } = useComboCart();

  // Find the specific combo
  const combo = useMemo(() => 
    comboData?.find((c: any) => c.combo_id === comboId),
    [comboData, comboId]
  );

  // Fetch combo data if not loaded
  useEffect(() => {
    if (!comboData && selectedRestaurant?.restaurant_id) {
      dispatch(fetchComboRequest());
    }
     dispatch(fetchTpnConfigRequest())
  }, [dispatch, comboData, selectedRestaurant?.restaurant_id]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [comboId]);

  const handleAddToCart = useCallback(async () => {
    if (!combo) return;

    setIsAddingToCart(true);
    
    try {
      handleComboAddToCart(combo);
    } catch (error) {
      console.error('Error adding combo to cart:', error);
    } finally {
      setIsAddingToCart(false);
    }
  }, [combo, handleComboAddToCart]);

  // UI States
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;
  if (!combo) return <ErrorState error="The requested combo could not be found." />;

  return (
    <main className="min-h-screen bg-gray-50" aria-busy={isAddingToCart}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header onBackClick={() => navigate('/' + window.location.search)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 pb-8">
          <LeftPanel
            combo={combo}
            formatPrice={formatPrice}
            onAddToCart={handleAddToCart}
            isAddingToCart={isAddingToCart}
            showAddToCart={shouldShowCart}
          />
          <RightPanel combo={combo} formatPrice={formatPrice} />
        </div>
      </div>
    </main>
  );
};

export default ComboDetailPage;
